// Broiler Batches filters + single-rule sort.
// Pure module: no React, no DB client (no `sb`), no browser globals. BroilerListView owns
// rendering; this file owns deterministic filter/sort behavior over the broiler
// batch records (app_store ppp-v4 shape).
//
// Right-sized for a small list (a handful to a few dozen batches): one active
// sort rule {key, dir}, a name/id text search, and a set of real-field filters
// over the planned/active/processed batches — status, breed, hatchery, brooder,
// schooner, hatch (start) date range, processing date range, and numeric ranges
// for bird count, birds arrived, to-processor count, cumulative mortality, and
// lbs produced.
//
// "Status" here is the EFFECTIVE poultry status. Callers pass a per-row
// resolver via ctx.statusOf (BroilerListView wires it to calcPoultryStatus) so
// the filter/sort agree with the badge the row renders. "lbsProduced" reads
// total feed produced via ctx.totalFeedLbsOf because feed is derived from daily
// reports the lib does not own.

export const BROILER_BATCH_STATUSES = Object.freeze(['planned', 'active', 'processed']);

export const BROILER_BATCH_FILTER_DIMENSIONS = Object.freeze([
  'textSearch',
  'status',
  'breed',
  'hatchery',
  'brooder',
  'schooner',
  'startDateRange',
  'processingDateRange',
  'birdCountRange',
  'birdsArrivedRange',
  'toProcessorRange',
  'mortalityRange',
  'lbsProducedRange',
]);

export const BROILER_BATCH_SORT_KEYS = Object.freeze([
  'batchName',
  'status',
  'startDate',
  'processingDate',
  'birdCount',
  'lbsProduced',
]);

// Default: processed batches newest-first (processing date descending). An
// UNSCHEDULED batch sorts to whichever end is "soonest" for the direction —
// last under desc, first under asc. See compareByKey's processingDate case.
export const BROILER_BATCH_DEFAULT_SORT = Object.freeze({key: 'processingDate', dir: 'desc'});

// The sort each status chip opens on (Ronnie, 2026-10-22: "soonest first").
// A slice of work still ahead reads soonest-first, because the batch going to
// the processor next is the one that needs attention; finished history reads
// newest-first, which is what the descending default was written for.
export const BROILER_BATCH_SORT_BY_CHIP: Readonly<Record<string, {key: string; dir: string}>> = Object.freeze({
  planned: Object.freeze({key: 'processingDate', dir: 'asc'}),
  in_process: Object.freeze({key: 'processingDate', dir: 'asc'}),
  complete: Object.freeze({key: 'processingDate', dir: 'desc'}),
  all: Object.freeze({key: 'processingDate', dir: 'desc'}),
});

export function broilerSortForStatusChip(chipKey: string | null | undefined) {
  return {...(BROILER_BATCH_SORT_BY_CHIP[String(chipKey)] || BROILER_BATCH_DEFAULT_SORT)};
}

const STATUS_ORDER = ['planned', 'active', 'processed'];

// The app_store ppp-v4 batch record, narrowed to the fields this module reads.
// The numeric columns are `number | string` because the store holds whatever
// the batch form last wrote — which is exactly why every read below goes
// through parseFloat rather than trusting the stored type.
export type BroilerBatchRecord = {
  name?: string | null;
  status?: string | null;
  breed?: string | null;
  hatchery?: string | null;
  brooder?: string | null;
  schooner?: string | null;
  hatchDate?: string | null;
  processingDate?: string | null;
  birdCount?: number | string | null;
  birdCountActual?: number | string | null;
  totalToProcessor?: number | string | null;
  mortalityCumulative?: number | string | null;
};

type BroilerNumericField = 'birdCount' | 'birdCountActual' | 'totalToProcessor' | 'mortalityCumulative';

export type BroilerNumericRange = {min?: number | string | null; max?: number | string | null};
export type BroilerDateRange = {after?: string | null; before?: string | null};

export type BroilerBatchFilters = {
  textSearch?: string | null;
  status?: readonly (string | null | undefined)[] | null;
  breed?: readonly (string | null | undefined)[] | null;
  hatchery?: readonly (string | null | undefined)[] | null;
  brooder?: readonly (string | null | undefined)[] | null;
  schooner?: readonly (string | null | undefined)[] | null;
  startDateRange?: BroilerDateRange | null;
  processingDateRange?: BroilerDateRange | null;
  birdCountRange?: BroilerNumericRange | null;
  birdsArrivedRange?: BroilerNumericRange | null;
  toProcessorRange?: BroilerNumericRange | null;
  mortalityRange?: BroilerNumericRange | null;
  lbsProducedRange?: BroilerNumericRange | null;
};

// The two per-row resolvers the view supplies (see the header note): the
// effective poultry status, and total feed lbs derived from daily reports.
export type BroilerBatchContext = {
  statusOf?: (batch: BroilerBatchRecord) => string;
  totalFeedLbsOf?: (batch: BroilerBatchRecord) => number | null | undefined;
};

export type BroilerBatchSortRule = {key?: string | null; dir?: string | null};

function statusFor(batch: BroilerBatchRecord, ctx: BroilerBatchContext | null | undefined): string {
  if (ctx && typeof ctx.statusOf === 'function') return ctx.statusOf(batch);
  return (batch && batch.status) || '';
}

function totalFeedLbsFor(batch: BroilerBatchRecord, ctx: BroilerBatchContext | null | undefined): number | null {
  if (ctx && typeof ctx.totalFeedLbsOf === 'function') {
    const v = ctx.totalFeedLbsOf(batch);
    return Number.isFinite(v) ? (v as number) : null;
  }
  return null;
}

function startDateFor(batch: BroilerBatchRecord | null | undefined): string | null {
  // Broiler batches anchor on the hatch date — there is no separate start_date
  // field. Hatch day is brooder-in (day 0), so it is the batch's start.
  return (batch && batch.hatchDate) || null;
}

function numField(batch: BroilerBatchRecord | null | undefined, key: BroilerNumericField): number | null {
  // parseFloat performs ToString on its argument, so a numerically-typed stored
  // value was always handled correctly here. The assertion states that instead
  // of inserting a String() call the runtime does not need.
  const v = parseFloat((batch && batch[key]) as string);
  return Number.isFinite(v) ? v : null;
}

// A numeric-range filter is {min, max} (either bound optional). A row with no
// value is excluded whenever any bound is set — a range filter only matches rows
// that actually have a value inside it (so a processing-date range never matches
// a planned batch, etc.).
function inNumRange(value: number | null, range: BroilerNumericRange | null | undefined): boolean {
  if (!range) return true;
  const hasMin = range.min != null && range.min !== '';
  const hasMax = range.max != null && range.max !== '';
  if (!hasMin && !hasMax) return true;
  if (value == null) return false;
  if (hasMin && value < Number(range.min)) return false;
  if (hasMax && value > Number(range.max)) return false;
  return true;
}

// A date-range filter is {after, before} (ISO yyyy-mm-dd strings, either bound
// optional). A row with no date is excluded whenever any bound is set.
function inDateRange(date: string | null | undefined, range: BroilerDateRange | null | undefined): boolean {
  if (!range) return true;
  const hasAfter = !!range.after;
  const hasBefore = !!range.before;
  if (!hasAfter && !hasBefore) return true;
  if (!date) return false;
  // Read through the property rather than the boolean alias: `hasAfter` and
  // `range.after` are the same truthiness test, but only the direct read tells
  // the compiler the bound is present. No comparison changed.
  if (hasAfter && range.after && date < range.after) return false;
  if (hasBefore && range.before && date > range.before) return false;
  return true;
}

// A multi-select filter is an array of accepted values (case-insensitive). An
// empty/absent array matches everything.
function arrayMatch(
  filterArr: readonly (string | null | undefined)[] | null | undefined,
  value: string | null | undefined,
): boolean {
  if (!Array.isArray(filterArr) || filterArr.length === 0) return true;
  const v = (value || '').toString().toLowerCase();
  return filterArr.some((x) => (x || '').toString().toLowerCase() === v);
}

export function buildBroilerBatchPredicate(filters: BroilerBatchFilters | null, ctx: BroilerBatchContext = {}) {
  const f = filters || {};

  return (batch: BroilerBatchRecord | null | undefined) => {
    if (!batch) return false;

    if (!arrayMatch(f.status, statusFor(batch, ctx))) return false;
    if (!arrayMatch(f.breed, batch.breed)) return false;
    if (!arrayMatch(f.hatchery, batch.hatchery)) return false;
    if (!arrayMatch(f.brooder, batch.brooder)) return false;
    if (!arrayMatch(f.schooner, batch.schooner)) return false;

    if (!inDateRange(startDateFor(batch), f.startDateRange)) return false;
    if (!inDateRange((batch && batch.processingDate) || null, f.processingDateRange)) return false;

    if (!inNumRange(numField(batch, 'birdCount'), f.birdCountRange)) return false;
    if (!inNumRange(numField(batch, 'birdCountActual'), f.birdsArrivedRange)) return false;
    if (!inNumRange(numField(batch, 'totalToProcessor'), f.toProcessorRange)) return false;
    if (!inNumRange(numField(batch, 'mortalityCumulative'), f.mortalityRange)) return false;
    if (!inNumRange(totalFeedLbsFor(batch, ctx), f.lbsProducedRange)) return false;

    if (typeof f.textSearch === 'string' && f.textSearch.trim()) {
      const q = f.textSearch.toLowerCase().trim();
      const fields = [batch.name, batch.breed, batch.hatchery, batch.schooner, batch.brooder].map((x) =>
        (x || '').toString().toLowerCase(),
      );
      if (!fields.some((x) => x.includes(q))) return false;
    }

    return true;
  };
}

export function buildBroilerBatchComparator(sortRule?: BroilerBatchSortRule | null, ctx: BroilerBatchContext = {}) {
  const rule =
    sortRule && sortRule.key && BROILER_BATCH_SORT_KEYS.includes(sortRule.key)
      ? sortRule
      : {...BROILER_BATCH_DEFAULT_SORT};
  const dir = rule.dir === 'desc' ? 'desc' : 'asc';

  return (a: BroilerBatchRecord, b: BroilerBatchRecord) => compareByKey(rule.key, a, b, dir, ctx);
}

function applyDir(cmp: number, dir: string): number {
  return dir === 'desc' ? -cmp : cmp;
}

function compareByKey(
  key: string | null | undefined,
  a: BroilerBatchRecord,
  b: BroilerBatchRecord,
  dir: string,
  ctx: BroilerBatchContext,
): number {
  switch (key) {
    case 'batchName': {
      const an = (a.name || '').trim();
      const bn = (b.name || '').trim();
      if (!an && !bn) return 0;
      if (!an) return 1;
      if (!bn) return -1;
      // Natural-ish compare so B-26-2 sorts before B-26-10.
      return applyDir(an.localeCompare(bn, undefined, {numeric: true, sensitivity: 'base'}), dir);
    }
    case 'status': {
      const ai = STATUS_ORDER.indexOf(statusFor(a, ctx));
      const bi = STATUS_ORDER.indexOf(statusFor(b, ctx));
      return applyDir((ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi), dir);
    }
    case 'startDate': {
      const ad = startDateFor(a);
      const bd = startDateFor(b);
      if (!ad && !bd) return 0;
      if (!ad) return 1;
      if (!bd) return -1;
      return applyDir(ad.localeCompare(bd), dir);
    }
    case 'processingDate': {
      // An unscheduled batch (no processing date) is treated as the SOONEST
      // thing on the list, not as missing data to be swept to the bottom.
      // Under asc — the order the In Process and Planned chips open on — it
      // therefore leads, because a batch nobody has booked a processing date
      // for is the one most likely to need a decision. Under desc it trails,
      // which keeps the finished-history default reading newest-first.
      // Before 2026-10-22 these rows fell to the end in BOTH directions, which
      // hid them in exactly the slice that cares about them.
      const ad = (a && a.processingDate) || '';
      const bd = (b && b.processingDate) || '';
      if (!ad && !bd) return 0;
      if (!ad) return dir === 'desc' ? 1 : -1;
      if (!bd) return dir === 'desc' ? -1 : 1;
      return applyDir(ad.localeCompare(bd), dir);
    }
    case 'birdCount': {
      const an = numField(a, 'birdCount');
      const bn = numField(b, 'birdCount');
      if (an == null && bn == null) return 0;
      if (an == null) return 1;
      if (bn == null) return -1;
      return applyDir(an - bn, dir);
    }
    case 'lbsProduced': {
      const av = totalFeedLbsFor(a, ctx);
      const bv = totalFeedLbsFor(b, ctx);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return applyDir(av - bv, dir);
    }
    default:
      return 0;
  }
}

// Breed dropdown options = active breed codes (the four known breed styles) plus
// any distinct breed code observed on the batches, so legacy/historical codes
// still surface. Returns [{code, label}] sorted by label.
export type BroilerBreedOption = {code: string; label: string};

export function broilerBreedFilterOptions(
  observedFromBatches: readonly (string | null | undefined)[] | null | undefined,
  breedLabelFn?: ((code: string) => string) | null,
): BroilerBreedOption[] {
  const labelFor = typeof breedLabelFn === 'function' ? breedLabelFn : (code: string) => code || '';
  const seen = new Set();
  const out = [];
  for (const code of ['CC', 'WR', 'FR', 'CY']) {
    seen.add(code.toLowerCase());
    out.push({code, label: labelFor(code)});
  }
  for (const value of observedFromBatches || []) {
    if (!value) continue;
    const key = String(value).toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({code: value, label: labelFor(value)});
  }
  out.sort((a, b) => a.label.localeCompare(b.label));
  return out;
}

// Distinct non-empty values for a batch field (hatchery / brooder / schooner),
// sorted, for populating a multi-select filter. Pure helper over the batch list.
export function broilerDistinctFieldValues(
  batches: readonly BroilerBatchRecord[] | null | undefined,
  key: keyof BroilerBatchRecord,
): string[] {
  const seen = new Set();
  const out = [];
  for (const batch of batches || []) {
    const value = batch && batch[key];
    if (value == null || value === '') continue;
    const str = String(value).trim();
    if (!str) continue;
    const lower = str.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push(str);
  }
  out.sort((a, b) => a.localeCompare(b, undefined, {sensitivity: 'base'}));
  return out;
}
